import React, {ChangeEvent, Component, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {AdminNotificationReceiveSettingRepo} from './AdminNotificationReceiveSettingRepo'
import {SessionStorageHelper} from '../../SessionStorageHelper'
import {
    AdminLoginInfoSessionStorageKey,
} from '../../__NoAutomatedTests/common/authentication/NatAdminLoginInfoSessionStorageHelper'
import MeetingNotificationReceiveSetting from './MeetingNotificationReceiveSetting'
import {Nullable} from '../../app/TypeHelpers'
import {COMMON_CONSTANTS} from '../../CommonConstants'
import SortableTableHeader, {
    SortableTableHeaderCell,
} from '../../styledComponents/adminPage/SortablePaginationTableHeader'
import SortablePaginationTable from '../../styledComponents/adminPage/SortablePaginationTable'
import SortablePaginationTableBody from '../../styledComponents/adminPage/SortablePaginationTableBody'
import {Box, Checkbox, FormControlLabel, InputAdornment, Switch, TableCell, TableRow, Tooltip} from '@mui/material'
import {ContentsForPagination} from '../shared/ContentsForPagination'
import {numberOrZero} from '../../stringHelper/stringHelpers'
import {PageMainArea} from '../shared/hoc/withAdminPageTemplate'
import NatCondominiumGroupRepo
    from '../../__NoAutomatedTests/components/condominiumGroup/repository/NatCondominiumGroupRepo'
import {
    CondominiumGroupData,
    NatGetCondominiumGroup,
} from '../../__NoAutomatedTests/components/condominiumGroup/repository/NatGetCondominiumGroup'
import {AdminDefaultMediumButton} from '../../styledComponents/adminPage/Buttons'
import {Messages} from '../../Messages'
import ListSearchFormArea from '../../styledComponents/adminPage/ListSearchFormArea'
import styled from 'styled-components'
import {
    FullWidthLeftRowFlexContainer,
    LeftRowFlexContainer,
    RowFlexContainer,
} from '../../styledComponents/FlexComponents'
import NatCondominiumGroupSelectComponent
    from '../../__NoAutomatedTests/components/common/NatCondominiumGroupSelectComponent'
import {Search} from '@mui/icons-material'
import PPInput from '../shared/forms/PPInput'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import moment, {Moment} from 'moment'
import {
    NatGetCurrentDateTime,
} from '../../__NoAutomatedTests/components/currentDateTime/repository/NatGetCurrentDateTime'
import {AVAILABLE_OR_NOT} from '../../CodeMaster'
import {FontAwesomeIconWrapper} from '../assocDirector/AssocDirectorCommon'
import {faBellSlash} from '@fortawesome/free-regular-svg-icons/faBellSlash'
import {NotificationReceiver} from './NotificationReceiver'
import {Colors} from '../../styledComponents/Constants'
import {KEY_MEETING_NOTIFICATION_RECEIVE_LIST_SEARCH_CONDITION} from '../../app/AdminSessionUtil'

interface Props {
    adminNotificationReceiveSettingRepo: AdminNotificationReceiveSettingRepo
    natCondominiumGroupRepo: NatCondominiumGroupRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
}

interface State {
    isLoadCompleted: boolean
    condominiumGroup: CondominiumGroupData
    selectedCondominiumGroupId: number
    keyword: string
    isOnlyEnabled: boolean
    rowsPerPage: number
    notificationReceiveSettingList: MeetingNotificationReceiveSetting[]
    totalCount: number
    page: number
    currentDateTime: Moment
}

interface SearchCondition {
    selectedCondominiumGroupId: number
    keyword: string
    isOnlyEnabled: boolean
    rowsPerPage: number
}

class AdminMeetingNotificationReceiveListPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        this.state = {
            isLoadCompleted: false,
            condominiumGroup: {
                level1CondominiumGroupId: '',
                level1CondominiumGroupName: '',
                level1CondominiumGroupVersion: 0,
                level2CondominiumGroupInfo: [],
            },
            selectedCondominiumGroupId: SessionStorageHelper.getNumberItemWithDefault(
                AdminLoginInfoSessionStorageKey.LOGIN_USER_CONDOMINIUM_GROUP_ID, -1,
            ),
            keyword: '',
            isOnlyEnabled: false,
            rowsPerPage: COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[0],
            notificationReceiveSettingList: [],
            totalCount: 0,
            page: 0,
            currentDateTime: moment(),
        }
    }

    async componentDidMount(): Promise<void> {
        const {asyncCallFunction, natCondominiumGroupRepo, currentDateTimeRepo} = this.props
        const {selectedCondominiumGroupId, keyword, isOnlyEnabled, rowsPerPage} = this.state

        await this.props.eventEmitter.showLoading()
        try {
            const condominiumGroupResponse: Nullable<NatGetCondominiumGroup> = await asyncCallFunction(
                natCondominiumGroupRepo.getCondominiumGroup.bind(natCondominiumGroupRepo),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )

            if (condominiumGroupResponse === null || condominiumGroupResponse.details === null) {
                return
            }
            this.setState({condominiumGroup: condominiumGroupResponse.details})

            const currentDateTimeResponce: Nullable<NatGetCurrentDateTime> = await asyncCallFunction(
                currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )

            if (currentDateTimeResponce === null || currentDateTimeResponce.details === null) {
                return
            }
            this.setState({currentDateTime: moment(currentDateTimeResponce.details.currentDateTime)})

            const searchCondition =
                SessionStorageHelper.getObject<SearchCondition>(KEY_MEETING_NOTIFICATION_RECEIVE_LIST_SEARCH_CONDITION)

            let searchConditionCondominiumGroupId = selectedCondominiumGroupId
            let searchConditionKeyword = keyword
            let searchConditionIsOnlyEnabled = isOnlyEnabled
            let searchConditionRowsPerPage = rowsPerPage
            if (searchCondition) {
                searchConditionCondominiumGroupId = searchCondition.selectedCondominiumGroupId
                searchConditionKeyword = searchCondition.keyword
                searchConditionIsOnlyEnabled = searchCondition.isOnlyEnabled
                searchConditionRowsPerPage = searchCondition.rowsPerPage
            }

            await this.searchMeetingNotificationReceiveList(
                searchConditionCondominiumGroupId,
                searchConditionKeyword,
                searchConditionIsOnlyEnabled,
                searchConditionRowsPerPage,
            )
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async searchMeetingNotificationReceiveList(
        condominiumGroupId: number,
        keyword: string,
        isOnlyEnabled: boolean,
        rowsPerPage: number,
        newPage: number = COMMON_CONSTANTS.FIRST_PAGE,
    ): Promise<void> {
        const {adminNotificationReceiveSettingRepo} = this.props
        const normalizedKeyword = keyword.replace(/\t/g, ' ')

        await this.props.eventEmitter.showLoading()
        try {
            const searchResult: Nullable<ContentsForPagination<MeetingNotificationReceiveSetting>> =
                await this.props.asyncCallFunction(
                    // @ts-ignore bindの引数が多すぎて警告になるためignore
                    adminNotificationReceiveSettingRepo.searchMeetingNotificationReceiveList.bind(
                        adminNotificationReceiveSettingRepo,
                        condominiumGroupId,
                        normalizedKeyword,
                        isOnlyEnabled,
                        rowsPerPage,
                        newPage * rowsPerPage,
                    ),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    },
                )

            if (searchResult === null) {
                return
            }

            const searchCondition: SearchCondition = {
                selectedCondominiumGroupId: condominiumGroupId,
                keyword: normalizedKeyword,
                isOnlyEnabled: isOnlyEnabled,
                rowsPerPage: rowsPerPage,
            }

            SessionStorageHelper.setObject(KEY_MEETING_NOTIFICATION_RECEIVE_LIST_SEARCH_CONDITION, searchCondition)

            this.setState({
                selectedCondominiumGroupId: condominiumGroupId,
                isLoadCompleted: true,
                keyword: normalizedKeyword,
                isOnlyEnabled: isOnlyEnabled,
                notificationReceiveSettingList: searchResult.contents,
                totalCount: searchResult.totalContentsCount,
                page: newPage,
                rowsPerPage,
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleChangeSelectedCondominiumGroupId = (selectedCondominiumGroupId: number) => {
        this.setState({selectedCondominiumGroupId})
    }

    handleChangeKeyword = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({keyword: event.target.value})
    }

    handleChangeIsOnlyEnabledCheckbox = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        this.setState({isOnlyEnabled: checked})
    }

    handleClickSearch = () => {
        const {selectedCondominiumGroupId, keyword, isOnlyEnabled, rowsPerPage} = this.state
        this.searchMeetingNotificationReceiveList(selectedCondominiumGroupId, keyword, isOnlyEnabled, rowsPerPage)
    }

    handleChangePage = async (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> => {
        const {selectedCondominiumGroupId, keyword, isOnlyEnabled, rowsPerPage} = this.state
        await this.searchMeetingNotificationReceiveList(
            selectedCondominiumGroupId,
            keyword,
            isOnlyEnabled,
            rowsPerPage,
            newPage,
        )
    }

    handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const newRowsPerPage: number = numberOrZero(event.target.value)

        const {selectedCondominiumGroupId, keyword, isOnlyEnabled} = this.state
        await this.searchMeetingNotificationReceiveList(
            selectedCondominiumGroupId,
            keyword,
            isOnlyEnabled,
            newRowsPerPage,
        )
    }

    handleChangeNotificationReceiveSettingSwitch = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ): Promise<void> => {
        const checked: boolean = event.target.checked
        const condominiumCd: string = event.target.value

        const {adminNotificationReceiveSettingRepo} = this.props
        const {notificationReceiveSettingList} = this.state

        await this.props.eventEmitter.showLoading()
        try {
            await this.props.asyncCallFunction(
                adminNotificationReceiveSettingRepo.updateNotificationReceiveSetting.bind(
                    adminNotificationReceiveSettingRepo,
                    condominiumCd,
                    checked,
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
            )

            const newSetting: Nullable<MeetingNotificationReceiveSetting> =
                await this.props.asyncCallFunction(
                    adminNotificationReceiveSettingRepo.getMeetingNotificationReceiveSetting.bind(
                        adminNotificationReceiveSettingRepo, condominiumCd,
                    ),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    },
                )

            const newSettingList = notificationReceiveSettingList
                .map(setting => setting.condominiumCd === condominiumCd ? newSetting : setting)
                .filter(setting => setting) as MeetingNotificationReceiveSetting[]

            this.setState({
                notificationReceiveSettingList: newSettingList,
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    renderSearchForm(): ReactNode {
        return <ListSearchFormArea onEnterKey={this.handleClickSearch} searchResult={
            this.state.totalCount === 0 ? {noResultMessage: Messages.IOPCC0014.toString()} : undefined
        }>
            <Box pl={'8px'}>
                <label>
                    <SearchRowContainer>
                        <SearchColumnContainer>物件グループ選択</SearchColumnContainer>
                        <SearchInputContainer>
                            <NatCondominiumGroupSelectComponent
                                condominiumGroup={this.state.condominiumGroup}
                                defaultValue={this.state.selectedCondominiumGroupId}
                                onChangeSelectedCondominiumGroupId={this.handleChangeSelectedCondominiumGroupId}
                                requiredFlag={true}
                            />
                        </SearchInputContainer>
                    </SearchRowContainer>
                </label>
                <label>
                    <SearchRowContainer>
                        <SearchColumnContainer>キーワード検索</SearchColumnContainer>
                        <SearchInputContainer>
                            <PPInput
                                value={this.state.keyword}
                                onChange={this.handleChangeKeyword}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <Search htmlColor={Colors.AccentDark}/>
                                    </InputAdornment>
                                }
                                inputProps={{
                                    minLength: 0,
                                    maxLength: 50,
                                }}
                                placeholder={'物件や通知受信者で検索できます'}
                                fullWidth
                                hint={
                                    '・物件コード、物件名、通知受信者で検索できます。\n' +
                                    '・キーワードの一部分でも検索できます。(あいまい検索)\n' +
                                    '・通知受信者をフルネームで指定する場合、姓と名の間にはスペースを入れないでください。\n' +
                                    '・複数のキーワードの指定はできません。'
                                }
                            />
                        </SearchInputContainer>
                    </SearchRowContainer>
                </label>
                <RowFlexContainer>
                    <IsOnlyCheckBoxControlLabel
                        label={'通知受信がONになっている物件のみを検索する'}
                        control={(<Checkbox
                            checked={this.state.isOnlyEnabled}
                            onChange={this.handleChangeIsOnlyEnabledCheckbox}
                            color="primary"/>)}
                    />
                    <SearchButton onClick={this.handleClickSearch}>
                        検索する
                    </SearchButton>
                </RowFlexContainer>
            </Box>
        </ListSearchFormArea>
    }

    renderSearchResultTable(settings: MeetingNotificationReceiveSetting[]): ReactNode {
        if (settings.length === 0) {
            return null
        }

        const {currentDateTime} = this.state

        const headers: SortableTableHeaderCell[] = [
            {id: 'condominiumCd', label: '物件コード'},
            {id: 'condominiumName', label: '物件名', align: 'left'},
            {id: 'condominiumGroupName', label: '物件グループ名', align: 'left'},
            {id: 'condominiumMailAddress', label: '物件の連絡先メールアドレス', align: 'left'},
            {id: 'notificationReceiver', label: '通知受信者', align: 'left'},
            {id: 'action', label: '通知を受信する'},
        ]
        return <PageMainArea>
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.totalCount,
                    rowsPerPage: this.state.rowsPerPage,
                    page: this.state.page,
                    onPageChange: this.handleChangePage,
                    onRowsPerPageChange: this.handleChangeRowsPerPage,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                />
                <SortablePaginationTableBody>
                    {settings.map((item, index) => <TableRow key={index}>
                        <TableCell align={'center'}>{item.condominiumCd}</TableCell>
                        <TableCell>{item.condominiumName}</TableCell>
                        <TableCell>{item.condominiumGroupName}</TableCell>
                        <TableCell>{item.condominiumMailAddressList.map((address, ai) =>
                            <div key={ai}>{address}</div>)}</TableCell>
                        <TableCell>{this.renderReceiverList(item.receiverList, currentDateTime)}</TableCell>
                        <TableCell align={'center'}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        key={item.condominiumCd}
                                        checked={item.isReceive}
                                        value={item.condominiumCd}
                                        color={'primary'}
                                        onChange={this.handleChangeNotificationReceiveSettingSwitch}
                                    />
                                }
                                label={<div>{item.isReceive ? 'ON' : 'OFF'}</div>}
                            />
                        </TableCell>
                    </TableRow>)}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        </PageMainArea>
    }

    renderReceiverList(receivers: NotificationReceiver[], currentDateTime: moment.Moment): ReactNode {
        return <Box minWidth={'245px'}>
            {receivers.map((receiver, ri) => {
                if (receiver.abolitionDatetime !== null) {
                    return null
                }

                const canLogin: boolean = receiver.loginAvailability === AVAILABLE_OR_NOT.AVAILABLE.value &&
                    (receiver.termStartDate === null || currentDateTime.isSameOrAfter(receiver.termStartDate, 'day')) &&
                    (receiver.termEndDate === null || currentDateTime.isSameOrBefore(receiver.termEndDate, 'day'))

                let tooltipTitle: ReactNode
                if (canLogin) {
                    if (!receiver.hasMailAddress) {
                        tooltipTitle = 'メールアドレス未登録のため、通知できません。'
                    }
                } else {
                    if (receiver.hasMailAddress) {
                        tooltipTitle = 'ログイン不可のため、通知できません。'
                    } else {
                        tooltipTitle = <>
                            <div>・メールアドレス未登録のため、通知できません。</div>
                            <div>・ログイン不可のため、通知できません。</div>
                        </>
                    }
                }

                return <ReceiverArea key={ri}>
                    {receiver.familyName} {receiver.firstName}
                    {tooltipTitle && <Box marginLeft={'8px'}>
                        <BellSlashIconWithTooltip tooltipTitle={tooltipTitle}/>
                    </Box>}
                </ReceiverArea>
            })}
        </Box>
    }

    render(): ReactNode {
        const {isLoadCompleted, notificationReceiveSettingList} = this.state
        if (!isLoadCompleted) {
            return null
        }

        return <>
            {this.renderSearchForm()}
            {this.renderSearchResultTable(notificationReceiveSettingList)}
        </>
    }
}

export default createAdminPage(AdminMeetingNotificationReceiveListPage)

const BellSlashIcon = () =>
    <FontAwesomeIconWrapper icon={faBellSlash} iconSize={'16px'} iconColor={Colors.AccentDark}/>

const BellSlashIconWithTooltip =
    React.forwardRef((props: { tooltipTitle: ReactNode }, ref: React.Ref<HTMLDivElement>) =>
        <Tooltip
            title={props.tooltipTitle ? props.tooltipTitle : ''}
            PopperProps={{disablePortal: true, transition: true}}
            arrow
        >
            <div ref={ref}>
                <BellSlashIcon/>
            </div>
        </Tooltip>,
    )

const SearchRowContainer = styled(FullWidthLeftRowFlexContainer)`
  padding: 8px 0;
`

const SearchColumnContainer = styled.div`
  width: 124px;
`

const SearchInputContainer = styled.div`
  margin-left: 16px;
  width: 500px;
`

const IsOnlyCheckBoxControlLabel = styled(FormControlLabel)`
  & .MuiTypography-root {
    font-size: 14px;
  }
`

const SearchButton = styled(AdminDefaultMediumButton)`
  &.MuiButtonBase-root {
    width: 205px;
  }
`

const ReceiverArea = styled(LeftRowFlexContainer)`
  line-height: 22px;
`
